import React, { useEffect, useState } from "react";
import InboxView from "../../components/InboxView";
import useActiveTenants from "../../hooks/useActiveTenants";
import { circlesApi } from "./circlesApi";
import { Modal, inr, shortDate, useBusy, useCirclesList } from "./circlesShared";
import "../../assets/css/PageHeading.css";

const slugify = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

const rateText = (p) => (p.Paid_Rates || []).map((r, i) => `L${i + 1} ${p.Commission_Type === "Fixed" ? inr(r) : `${r}%`}`).join(" · ") || "—";

const EMPTY = {
  Tenant_Id: "",
  Tenant_Project_Id: "",
  Domain_Id: "",
  Name: "",
  Slug: "",
  Headline: "",
  Project_Type: "",
  Location: "",
  Description: "",
  Commission_Type: "Percent",
  Level_Rates: "2",
  Valid_From: "",
  Valid_To: "",
  Is_Open: false,
  Is_App: false,
  App_Name: "",
  Android_URL: "",
  iOS_URL: "",
  Gift_Label: "",
};

function ProjectModal({ project, maxLevels, onClose, onDone }) {
  const tenants = useActiveTenants();
  const [form, setForm] = useState(() =>
    project
      ? {
          ...EMPTY,
          ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, project[k] ?? EMPTY[k]])),
          Tenant_Id: String(project.Tenant_Id),
          Tenant_Project_Id: project.Tenant_Project_Id ? String(project.Tenant_Project_Id) : "",
          Domain_Id: project.Domain_Id ? String(project.Domain_Id) : "",
          Level_Rates: (project.Level_Rates || []).join(", "),
        }
      : EMPTY
  );
  const [projects, setProjects] = useState([]);
  const [domains, setDomains] = useState([]);
  const { busy, error, run } = useBusy();
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    circlesApi.domains().then(setDomains).catch(() => setDomains([]));
  }, []);
  useEffect(() => {
    let alive = true;
    if (!form.Tenant_Id) return undefined;
    circlesApi
      .tenantProjects(form.Tenant_Id)
      .then((rows) => alive && setProjects(rows))
      .catch(() => alive && setProjects([]));
    return () => {
      alive = false;
    };
  }, [form.Tenant_Id]);

  const submit = () =>
    run(async () => {
      const rates = String(form.Level_Rates)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .map(Number);
      const body = {
        ...form,
        Tenant_Id: Number(form.Tenant_Id) || null,
        Tenant_Project_Id: form.Tenant_Project_Id ? Number(form.Tenant_Project_Id) : null,
        Domain_Id: form.Domain_Id ? Number(form.Domain_Id) : null,
        Level_Rates: rates,
      };
      if (project) await circlesApi.updateProject(project.Circles_Project_Id, body);
      else await circlesApi.createProject(body);
      onDone();
    });

  return (
    <Modal
      title={project ? `Edit ${project.Name}` : "Open a project to Circles"}
      onClose={onClose}
      busy={busy}
      onSubmit={submit}
      footer={
        <>
          <button type="button" className="wl-btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="wl-btn wl-btn--primary" disabled={busy}>
            {project ? "Save" : "Create"}
          </button>
        </>
      }
    >
      <div className="cx-row">
        <label className="wl-field">
          <span>Client</span>
          <select value={form.Tenant_Id} onChange={(e) => set({ Tenant_Id: e.target.value, Tenant_Project_Id: "" })} disabled={!!project}>
            <option value="">Choose a client…</option>
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </label>
        <label className="wl-field">
          <span>Client's project (optional)</span>
          <select value={form.Tenant_Project_Id} onChange={(e) => set({ Tenant_Project_Id: e.target.value })} disabled={!form.Tenant_Id}>
            <option value="">None</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="cx-row">
        <label className="wl-field">
          <span>Name agents see</span>
          <input value={form.Name} onChange={(e) => set({ Name: e.target.value, Slug: project ? form.Slug : slugify(e.target.value) })} />
        </label>
        <label className="wl-field">
          <span>Link name</span>
          <input className="cx-mono" value={form.Slug} onChange={(e) => set({ Slug: slugify(e.target.value) })} placeholder="green-meadows" />
        </label>
      </div>
      <label className="wl-field">
        <span>Headline</span>
        <input value={form.Headline} onChange={(e) => set({ Headline: e.target.value })} placeholder="Gated villa plots, 200-500 sq yd" />
      </label>
      <div className="cx-row">
        <label className="wl-field">
          <span>Type</span>
          <input value={form.Project_Type} onChange={(e) => set({ Project_Type: e.target.value })} placeholder="Villa Plots" />
        </label>
        <label className="wl-field">
          <span>Location</span>
          <input value={form.Location} onChange={(e) => set({ Location: e.target.value })} placeholder="Shankarpally, Hyderabad" />
        </label>
        <label className="wl-field">
          <span>Business type</span>
          <select value={form.Domain_Id} onChange={(e) => set({ Domain_Id: e.target.value })}>
            <option value="">Client's own</option>
            {domains.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="wl-field">
        <span>Description</span>
        <textarea rows={3} value={form.Description} onChange={(e) => set({ Description: e.target.value })} />
      </label>
      <div className="cx-row">
        <label className="wl-field">
          <span>Commission</span>
          <select value={form.Commission_Type} onChange={(e) => set({ Commission_Type: e.target.value })}>
            <option value="Percent">% of deal value</option>
            <option value="Fixed">Fixed ₹ per sale</option>
          </select>
        </label>
        <label className="wl-field">
          <span>Rate{maxLevels > 1 ? "s by level (L1, L2…)" : " (Level 1)"}</span>
          <input value={form.Level_Rates} onChange={(e) => set({ Level_Rates: e.target.value })} placeholder={form.Commission_Type === "Fixed" ? "5000" : "2"} />
        </label>
      </div>
      <div className="cx-row">
        <label className="wl-field">
          <span>Valid from</span>
          <input type="date" value={form.Valid_From || ""} onChange={(e) => set({ Valid_From: e.target.value })} />
        </label>
        <label className="wl-field">
          <span>Valid to</span>
          <input type="date" value={form.Valid_To || ""} onChange={(e) => set({ Valid_To: e.target.value })} />
        </label>
      </div>
      <label className="cx-check">
        <input type="checkbox" checked={form.Is_App} onChange={(e) => set({ Is_App: e.target.checked })} />
        <span>This is a customer app (agents earn on confirmed app registrations)</span>
      </label>
      {form.Is_App ? (
        <>
          <div className="cx-row">
            <label className="wl-field">
              <span>App name</span>
              <input value={form.App_Name} onChange={(e) => set({ App_Name: e.target.value })} placeholder="Junior Eats" />
            </label>
            <label className="wl-field">
              <span>Customer's gift</span>
              <input value={form.Gift_Label} onChange={(e) => set({ Gift_Label: e.target.value })} placeholder="₹100 off your first order" />
            </label>
          </div>
          <div className="cx-row">
            <label className="wl-field">
              <span>Google Play link</span>
              <input value={form.Android_URL} onChange={(e) => set({ Android_URL: e.target.value })} placeholder="https://play.google.com/store/apps/details?id=…" />
            </label>
            <label className="wl-field">
              <span>App Store link</span>
              <input value={form.iOS_URL} onChange={(e) => set({ iOS_URL: e.target.value })} placeholder="https://apps.apple.com/…" />
            </label>
          </div>
        </>
      ) : null}
      <label className="cx-check">
        <input type="checkbox" checked={form.Is_Open} onChange={(e) => set({ Is_Open: e.target.checked })} />
        <span>Open to Circles agents</span>
      </label>
      {maxLevels === 1 ? <p className="cx-hint">Only Level 1 is paid today (Circles settings). Extra levels are ignored until you raise the level count.</p> : null}
      {error ? <div className="wl-alert wl-alert--error">{error}</div> : null}
    </Modal>
  );
}

export default function CirclesProjects() {
  const [editing, setEditing] = useState(null);
  const [notice, setNotice] = useState(null);
  const { rows, extra, loading, error, reload } = useCirclesList(() => circlesApi.projects(), []);
  const maxLevels = extra.settings?.maxLevels || 1;
  const newKey = async (p) => {
    const warn = p.App_Key_Hint ? " The old key stops working at once, so update the app's backend straight away." : "";
    if (!window.confirm(`Create a key for the ${p.App_Name} app's backend?${warn}`)) return;
    try {
      const res = await circlesApi.newAppKey(p.Circles_Project_Id);
      setNotice({ type: "success", text: `${p.App_Name} app key (shown once; send it as X-Circles-App-Key): ${res.data.App_Key}` });
      reload();
    } catch (err) {
      setNotice({ type: "error", text: err.message });
    }
  };

  return (
    <div className="p-2 p-md-3">
      <div className="co-page-head">
        <h2 className="co-page-head__title">Circles Projects</h2>
      </div>
      <InboxView
        onRefresh={reload}
        toolbarExtra={
          <button type="button" className="wl-btn wl-btn--primary wl-btn--sm" onClick={() => setEditing("new")}>
            Open a project
          </button>
        }
        notice={notice}
        onDismissNotice={() => setNotice(null)}
        error={error}
        loading={loading}
        rows={rows}
        rowKey={(p) => p.Circles_Project_Id}
        empty="No projects are open to Circles yet."
        columns={[
          { key: "name", label: "Project", render: (p) => (<><div className="wl-name">{p.Name}</div><div className="wl-sub cx-mono">/p/{p.Slug}</div></>) },
          { key: "client", label: "Client · business type", render: (p) => (<><div>{p.Tenant_Name}</div><div className="wl-sub">{p.Domain_Name || "—"}</div></>) },
          { key: "kind", label: "Kind", render: (p) => (p.Is_App ? `App · ${p.Gift_Label || "no gift"}` : p.Project_Type || "Project") },
          { key: "rates", label: "Commission", render: rateText },
          { key: "valid", label: "Valid", render: (p) => `${shortDate(p.Valid_From)} – ${shortDate(p.Valid_To)}` },
          { key: "stats", label: "Clicks · leads", render: (p) => `${p.Clicks} · ${p.Leads}` },
          { key: "open", label: "Status", render: (p) => <span className={`cx-badge cx-badge--${p.Is_Open ? "success" : "neutral"}`}>{p.Is_Open ? "Open" : "Closed"}</span> },
          { key: "actions", label: "Actions", className: "wl-actions-col", render: (p) => (
            <div className="cx-actions">
              <button type="button" className="wl-btn wl-btn--sm" onClick={() => setEditing(p)}>
                Edit
              </button>
              {p.Is_App ? (
                <button type="button" className="wl-btn wl-btn--sm" title={p.App_Key_Hint ? `Current key ends …${p.App_Key_Hint}` : "No key yet"} onClick={() => newKey(p)}>
                  {p.App_Key_Hint ? "New app key" : "Create app key"}
                </button>
              ) : null}
            </div>
          ) },
        ]}
        mobileCard={(p) => ({
          title: p.Name,
          meta: [{ label: p.Is_Open ? "Open" : "Closed", tone: p.Is_Open ? "success" : "neutral" }],
          fields: [
            { label: "Client", value: p.Tenant_Name },
            { label: "Commission", value: rateText(p) },
            { label: "Clicks · leads", value: `${p.Clicks} · ${p.Leads}` },
          ],
          actions: (
            <button type="button" className="wl-btn wl-btn--sm" onClick={() => setEditing(p)}>
              Edit
            </button>
          ),
        })}
      />
      {editing ? (
        <ProjectModal
          project={editing === "new" ? null : editing}
          maxLevels={maxLevels}
          onClose={() => setEditing(null)}
          onDone={() => {
            setNotice({ type: "success", text: editing === "new" ? "Project created." : "Project saved." });
            setEditing(null);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}
